import { useMemo } from 'react';
import { api } from '@ardoq/api';
import { getArdoqErrorMessage, isArdoqError } from '@ardoq/common-helpers';
import { type I18nLocale } from '@ardoq/i18n';
import { GenerateComponentDescription } from '../types';
import {
  buildRichTextEditorLabels,
  RichTextEditor,
  RichTextEditorProps,
  useGenerateDescriptionState,
} from '@ardoq/rich-text-editor';

export type SurveyTextAreaContainerProps = RichTextEditorProps & {
  componentTypeName: string;
  componentName: string | null;
  attributeName?: string;
  isReference?: boolean;
  isRequired?: boolean;
  hasAutocompleteFeature: boolean;
  generateComponentDescription: GenerateComponentDescription;
  onDescriptionReceived?: VoidFunction;
  onLLMCouldNotGenerateDescription?: (reason: string) => void;
  i18nLocale: I18nLocale;
};

export const SurveyTextAreaContainer = ({
  ariaLabel,
  attributeName,
  componentName,
  componentTypeName,
  content,
  onInputChange,
  onBlur,
  errorMessage,
  warningMessage,
  isReference,
  hasAutocompleteFeature,
  generateComponentDescription,
  onDescriptionReceived,
  onLLMCouldNotGenerateDescription,
  i18nLocale,
  labels,
}: SurveyTextAreaContainerProps) => {
  const resolvedLabels = useMemo(
    () => labels ?? buildRichTextEditorLabels(i18nLocale),
    [labels, i18nLocale]
  );
  const { state, commands } = useGenerateDescriptionState();
  if (
    !hasAutocompleteFeature ||
    isReference ||
    attributeName !== 'description'
  ) {
    return (
      <RichTextEditor
        ariaLabel={ariaLabel}
        dataTestId="rich-text-editor"
        content={content}
        onInputChange={onInputChange}
        onBlur={onBlur}
        errorMessage={errorMessage}
        warningMessage={
          warningMessage || state.reasonForNotBeingAbleToGenerateDescription
        }
        minHeight="230px"
        labels={resolvedLabels}
      />
    );
  }
  const generateDescription = async () => {
    if (!componentName) return;
    commands.onLoading();
    const response = await generateComponentDescription({
      name: componentName,
      type: componentTypeName,
      description: content || null,
    });
    commands.onDescriptionReceived();
    if (onDescriptionReceived) {
      onDescriptionReceived();
    }
    if (isArdoqError(response)) {
      commands.onError(getArdoqErrorMessage(response));
      api.logErrorIfNeeded(response);
    } else if (response.type === 'description') {
      onInputChange(response.text);
    } else {
      commands.onLLMCouldNotGenerateDescription(response.text);

      if (onLLMCouldNotGenerateDescription) {
        onLLMCouldNotGenerateDescription(response.text);
      }
    }
  };

  return (
    <RichTextEditor
      ariaLabel={ariaLabel}
      dataTestId="rich-text-editor"
      content={content}
      onInputChange={onInputChange}
      onBlur={onBlur}
      errorMessage={
        errorMessage || state.reasonForNotBeingAbleToGenerateDescription
      }
      warningMessage={warningMessage}
      generateDescriptionProps={{
        onClick: generateDescription,
        isLoading: state.isLoading,
        hasError: state.hasError,
        isMissingItemToDescribe: !componentName,
      }}
      labels={resolvedLabels}
    />
  );
};
